import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NumericFormat } from 'react-number-format'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box, MenuItem, Select, TextField, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'
import { useDebouncedCallback } from 'use-debounce'

import { useGetOrderItemsQuery, useLazyGetOrderItemsQuery, useUpdateOrderItemMutation } from '@/app/services/orderItems'
import { selectLocale } from '@/app/slices/appSlice'
import { addItemToUpdateQueue, removeItemFromUpdateQueue } from '@/app/slices/orderItemsSlice'
import { selectCurrencyCode, selectOrganisationId } from '@/app/slices/organisationSlice'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'
import { getCurrencyFormat } from '@/common/utils'

const Folding = ({ orderItemId }) => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { orderId } = useParams()
    const dispatch = useDispatch()

    const organisationId = useSelector(selectOrganisationId)

    const currencyCode = useSelector(selectCurrencyCode)
    const locale = useSelector(selectLocale)

    const { orderItem } = useGetOrderItemsQuery(
        {
            organisationId,
            orderId,
        },
        {
            selectFromResult: ({ data }) => ({
                orderItem: data?.find((item) => item.id === orderItemId),
            }),
        }
    )

    const [getOrderItems] = useLazyGetOrderItemsQuery()
    const [updateOrderItem] = useUpdateOrderItemMutation()

    const [itemSource, setItemSource] = useState(orderItem?.foldingSource || '')
    const [itemMaxLength, setItemMaxLength] = useState(orderItem?.maxFoldLength || '')
    const [itemNumberOfFolds, setItemNumberOfFolds] = useState(orderItem?.numberOfFolds || '')

    const [maxLengthError, setMaxLengthError] = useState(!itemMaxLength)
    const [numberOfFoldsError, setNumberOfFoldsError] = useState(!itemNumberOfFolds)

    const units = orderItem?.isImperial ? 'in' : 'mm'

    const isPartFromLibrary = Boolean(orderItem?.partLibraryEntryId)
    const currencyFormat = getCurrencyFormat(currencyCode, locale)

    const handleUpdateOrderItem = async (orderItem) => {
        try {
            await updateOrderItem({
                organisationId,
                orderId,
                orderItemId: orderItem?.id,
                orderItem,
            }).unwrap()
            dispatch(removeItemFromUpdateQueue(orderItemId))
        } catch (error) {
            enqueueSnackbar(t('Failed to update the order item'), {
                variant: 'error',
            })
        }
    }

    const debounceHandleUpdateOrderItem = useDebouncedCallback(handleUpdateOrderItem, 1500)

    const handleSourceChange = async (event) => {
        const value = event?.target?.value
        if (Boolean(value) && itemSource !== value) {
            setItemSource(value)

            await handleUpdateOrderItem({
                ...orderItem,
                foldingSource: value,
            })

            if (value === 'Drawing') {
                await getOrderItems({ organisationId, orderId })
            }
        }
    }

    const handleMaxLengthChange = async (values) => {
        debounceHandleUpdateOrderItem.cancel()
        dispatch(addItemToUpdateQueue(orderItemId))

        const { floatValue } = values
        setMaxLengthError(!floatValue || isNaN(floatValue) || floatValue === 0)
        setItemMaxLength(floatValue)

        if (!isNaN(floatValue) && floatValue !== 0 && itemNumberOfFolds !== 0) {
            debounceHandleUpdateOrderItem({
                ...orderItem,
                maxFoldLength: floatValue,
                numberOfFolds: itemNumberOfFolds,
            })
        }
    }

    const handleNumberOfFoldsChange = async (values) => {
        debounceHandleUpdateOrderItem.cancel()
        dispatch(addItemToUpdateQueue(orderItemId))

        const { floatValue } = values
        setNumberOfFoldsError(!floatValue || isNaN(floatValue) || floatValue === 0)
        setItemNumberOfFolds(floatValue)

        if (!isNaN(floatValue) && floatValue !== 0 && itemMaxLength !== 0) {
            debounceHandleUpdateOrderItem({
                ...orderItem,
                numberOfFolds: floatValue,
                maxFoldLength: itemMaxLength,
            })
        }
    }

    useEffect(() => {
        if (!orderItem) return
        setItemMaxLength(orderItem?.maxFoldLength)
        setItemSource(orderItem?.foldingSource)
        setItemNumberOfFolds(orderItem?.numberOfFolds)
    }, [orderItem, orderItem?.maxFoldLength, orderItem?.foldingSource, orderItem?.numberOfFolds])

    return (
        <Box sx={{ padding: 1 }}>
            <Typography
                mb={1.5}
                variant="body2"
            >
                {t('Folding')}
            </Typography>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'space-between',
                    mb: 1,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <Typography
                        color="text.secondary"
                        variant="small"
                    >
                        {t('Input mode')}
                    </Typography>
                </Box>
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <Box
                        sx={{
                            flex: 1,
                        }}
                    >
                        <Select
                            data-testid="order-item-source"
                            disabled={isPartFromLibrary}
                            id="select-itemSource"
                            labelId="select-itemSource-label"
                            size="small"
                            sx={{
                                '& .MuiSelect-select': { fontSize: '14px', paddingBlock: '5px', paddingInline: '12px' },
                            }}
                            value={itemSource}
                            fullWidth
                            onChange={handleSourceChange}
                        >
                            <MenuItem
                                key="drawing"
                                value="Drawing"
                            >
                                {t('From drawing')}
                            </MenuItem>
                            <MenuItem
                                key="manual"
                                value="Manual"
                            >
                                {t('Manual input')}
                            </MenuItem>
                        </Select>
                    </Box>
                </TbxTooltip>
            </Box>

            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'space-between',
                    mb: 1,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <Typography
                        color="text.secondary"
                        variant="small"
                    >
                        {t('Longest fold')}
                    </Typography>
                </Box>
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <Box
                        sx={{
                            flex: 1,
                        }}
                    >
                        <NumericFormat
                            allowNegative={false}
                            customInput={TextField}
                            decimalSeparator={currencyFormat.decimal}
                            disabled={itemSource === 'Drawing' || isPartFromLibrary}
                            error={maxLengthError}
                            placeholder={`0 ${units}`}
                            size="small"
                            slotProps={{
                                input: {
                                    style: { fontSize: '14px' },
                                },
                                htmlInput: {
                                    style: { textAlign: 'right', paddingBlock: '6px', paddingInline: '12px' },
                                    'data-testid': 'order-item-max-length-quantity',
                                },
                            }}
                            suffix={` ${units}`}
                            thousandSeparator={currencyFormat.group}
                            value={itemMaxLength}
                            variant="outlined"
                            onValueChange={handleMaxLengthChange}
                        />
                    </Box>
                </TbxTooltip>
            </Box>

            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'space-between',
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <Typography
                        color="text.secondary"
                        variant="small"
                    >
                        {t('Number of folds')}
                    </Typography>
                </Box>
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <Box
                        sx={{
                            flex: 1,
                        }}
                    >
                        <NumericFormat
                            allowNegative={false}
                            customInput={TextField}
                            decimalSeparator={currencyFormat.decimal}
                            disabled={itemSource === 'Drawing' || isPartFromLibrary}
                            error={numberOfFoldsError}
                            placeholder="0"
                            size="small"
                            slotProps={{
                                input: {
                                    style: { fontSize: '14px' },
                                },
                                htmlInput: {
                                    style: { textAlign: 'right', paddingBlock: '6px', paddingInline: '12px' },
                                    'data-testid': 'order-item-number-of-folds-quantity',
                                },
                            }}
                            thousandSeparator={currencyFormat.group}
                            value={itemNumberOfFolds}
                            variant="outlined"
                            onValueChange={handleNumberOfFoldsChange}
                        />
                    </Box>
                </TbxTooltip>
            </Box>
        </Box>
    )
}

Folding.propTypes = {
    orderItemId: PropTypes.string,
}

export default Folding
